import React, { useMemo } from 'react';
import './Shifts.css';

const CalendarGrid = ({ shifts, employees, currentDate, calendarView, searchTerm, onDayClick }) => {

  const isWorkingShift = (shift) => {
    // Показываем все смены, включая выходные, отпуск и больничные
    // Пользователи должны видеть все назначенные смены
    return true;
  };

  // Перенесем функции до useMemo
  const generateMonthCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const startDate = new Date(firstDay);
    const dayOfWeek = startDate.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startDate.setDate(startDate.getDate() + daysToMonday);

    const calendar = [];
    const today = new Date();

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      const isCurrentMonth = date.getMonth() === month;
      calendar.push(createCalendarDay(date, today, isCurrentMonth));
    }

    return calendar;
  };

  const generateWeekCalendar = () => {
    const calendar = [];
    const today = new Date();

    const monday = new Date(currentDate);
    const dayOfWeek = monday.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    monday.setDate(monday.getDate() + daysToMonday);

    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      calendar.push(createCalendarDay(date, today, true));
    }

    return calendar;
  };

  const createCalendarDay = (date, today, isCurrentMonth) => {
    const isToday = date.toDateString() === today.toDateString();

    let userShifts = shifts.filter(shift => {
      const shiftDate = new Date(shift.start_time);
      const dateMatch = shiftDate.toDateString() === date.toDateString();

      // Фильтруем только рабочие смены
      const isWorking = isWorkingShift(shift);

      if (searchTerm) {
        const searchMatch = shift.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (shift.user_position && shift.user_position.toLowerCase().includes(searchTerm.toLowerCase()));
        return dateMatch && searchMatch && isWorking;
      }

      return dateMatch && isWorking;
    });

    // Дедупликация: группируем по user_id, берём первую смену сотрудника
    const deduplicatedShifts = [];
    const seenUsers = new Set();
    for (const shift of userShifts) {
      if (!seenUsers.has(shift.user_id)) {
        seenUsers.add(shift.user_id);
        deduplicatedShifts.push(shift);
      }
    }

    const hasShifts = deduplicatedShifts.length > 0;

    return {
      date,
      day: date.getDate(),
      isToday,
      hasShifts,
      shifts: deduplicatedShifts,
      shiftCount: deduplicatedShifts.length,
      isCurrentMonth
    };
  };

  const getWeekDays = () => {
    return ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  };

  const getShiftShortType = (shift) => {
    if (!shift.description) return 'С';
    if (shift.description.includes('Дневная смена (08:00 - 20:00)')) return 'Д';
    if (shift.description.includes('Дневная смена (09:00 - 18:00)')) return 'Д8';
    if (shift.description.includes('Дневная смена (09:00 - 21:00)')) return 'Д9-21';
    if (shift.description.includes('Дневная смена (11:00 - 20:00)')) return 'Д11-20';
    if (shift.description.includes('Дневная смена (11:00 - 23:00)')) return 'Д11-23';
    if (shift.description.includes('Ночная смена')) return 'Н';
    if (shift.description.includes('Отпуск') && !shift.description.includes('без сохранения')) return 'ОТ';
    if (shift.description.includes('без сохранения')) return 'ДО';
    if (shift.description.includes('Выходной')) return 'В';
    if (shift.description.includes('Больничный')) return 'Б';
    if (shift.description.includes('праздничный')) return 'НВ';
    return 'С';
  };

  const getShiftCellClass = (shift) => {
    if (!shift.description) return 'day-shift';
    if (shift.description.includes('Дневная смена')) return 'day-shift';
    if (shift.description.includes('Ночная смена')) return 'night-shift';
    if (shift.description.includes('Отпуск')) return 'vacation-shift';
    if (shift.description.includes('без сохранения')) return 'unpaid-shift';
    if (shift.description.includes('Выходной')) return 'dayoff-shift';
    if (shift.description.includes('Больничный')) return 'sick-shift';
    if (shift.description.includes('праздничный')) return 'holiday-shift';
    return 'day-shift';
  };

  // Теперь useMemo может использовать функции
  const generateCalendar = useMemo(() => {
    if (calendarView === 'month') {
      return generateMonthCalendar();
    } else {
      return generateWeekCalendar();
    }
  }, [currentDate, calendarView, shifts, searchTerm]);

  const calendarData = generateCalendar;

  return (
    <div className="calendar-grid-container">
      {searchTerm && (
        <div className="search-results-info">
          <div className="search-info">
            <span className="search-term">
              Поиск: "{searchTerm}"
            </span>
            <span className="shifts-count">
              Найдено смен в календаре: {
                shifts.filter(shift => 
                  shift.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  (shift.user_position && shift.user_position.toLowerCase().includes(searchTerm.toLowerCase()))
                ).length
              }
            </span>
          </div>
        </div>
      )}

      <div className="calendar-grid">
        {getWeekDays().map(day => (
          <div key={day} className="calendar-weekday">{day}</div>
        ))}
        
        {calendarData.map((day, index) => {
          const hasSearchMatch = searchTerm && day.hasShifts;
          
          return (
            <div
              key={index}
              className={`calendar-day ${!day ? 'empty' : ''} ${day?.isToday ? 'today' : ''} ${day?.hasShifts ? 'has-shifts' : ''} ${hasSearchMatch ? 'search-match' : ''} ${day && !day.isCurrentMonth ? 'other-month' : ''}`}
              onClick={() => day && onDayClick(day)}
            >
              {day && (
                <>
                  <div className="day-number">{day.day}</div>
                  {day.hasShifts && (
                    <div className="day-shifts">
                      {day.shifts.map(shift => (
                        <div
                          key={shift.id}
                          className={`shift-badge ${getShiftCellClass(shift)}`}
                          title={`${shift.user_name}: ${getShiftShortType(shift)} - ${shift.description}`}
                        >
                          {getShiftShortType(shift)}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarGrid;